/**
 * Aba Perfil (C1) — fiel ao mockup (.set-section / .set-avatar / .set-fields).
 * Reusa o backend existente: /api/me (PATCH) + /api/me/foto (POST/DELETE) via
 * AuthContext (updateProfile/uploadPhoto/removePhoto).
 */
import React, { useEffect, useRef, useState } from "react";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { useAuthenticatedImageUrl } from "../../../hooks/useAuthenticatedImageUrl";

function iniciais(nome) {
  if (!nome) return "EU";
  const p = nome.trim().split(/\s+/);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || p[0]?.[1] || "")).toUpperCase();
}

export default function PerfilTab() {
  const { usuario, updateProfile, uploadPhoto, removePhoto } = useAuth();
  const toast = useToast();
  const fileRef = useRef(null);
  const { imageUrl } = useAuthenticatedImageUrl(usuario?.foto_url);

  const [form, setForm] = useState({ nome: "", cargo_exibido: "", descricao: "" });
  useEffect(() => {
    setForm({
      nome: usuario?.nome || "",
      cargo_exibido: usuario?.cargo_exibido || "",
      descricao: usuario?.descricao || "",
    });
  }, [usuario]);

  async function handlePhotoChange(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      await uploadPhoto(file);
      toast.success("Foto atualizada.");
    } catch (err) {
      toast.error(err?.message || "Falha ao enviar foto.");
    }
  }

  return (
    <section className="set-section">
      <div className="set-section__head">
        <h2 className="set-section__title">Perfil</h2>
        <p className="set-section__desc">
          Veja e edite suas informações pessoais — nome, foto e cargo exibido na organização.
        </p>
      </div>
      <div className="set-section__body">
        {/* Foto */}
        <div className="set-row">
          <div>
            <div className="set-row__lbl">Foto de perfil</div>
            <div className="set-row__desc">PNG ou JPG, até 2 MB. Aparece no menu, comentários e auditoria.</div>
          </div>
          <div className="set-row__control">
            <div className="set-avatar">
              <div className={`set-avatar__img${imageUrl ? " has-img" : ""}`}>
                {imageUrl ? <img src={imageUrl} alt={usuario?.nome || "Foto"} /> : iniciais(usuario?.nome)}
              </div>
              <div>
                <div className="set-avatar__actions">
                  <button className="btn btn--secondary btn--sm" type="button" onClick={() => fileRef.current?.click()}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
                    Enviar foto
                  </button>
                  {usuario?.foto_url ? (
                    <AsyncButton
                      variant="secondary"
                      onClick={() => removePhoto()}
                      successMessage="Foto removida."
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14 }}><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
                      Remover
                    </AsyncButton>
                  ) : null}
                </div>
                <div className="set-avatar__hint">Recomendado: imagem quadrada 400×400px</div>
              </div>
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg" hidden onChange={handlePhotoChange} />
          </div>
        </div>

        {/* Informações pessoais */}
        <div className="set-row">
          <div>
            <div className="set-row__lbl">Informações pessoais</div>
            <div className="set-row__desc">Mantenha seus dados atualizados para que a equipe te identifique.</div>
          </div>
          <div className="set-row__control">
            <div className="set-fields">
              <div className="input-group">
                <label className="input-label">Nome</label>
                <input
                  className="input"
                  value={form.nome}
                  onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                />
              </div>
              <div className="input-group">
                <label className="input-label">Cargo exibido</label>
                <input
                  className="input"
                  value={form.cargo_exibido}
                  onChange={(e) => setForm((f) => ({ ...f, cargo_exibido: e.target.value }))}
                />
              </div>
              <div className="input-group col-2">
                <label className="input-label">Email</label>
                {/* Email NAO eh editavel pelo usuario (gerenciado pelo admin).
                    Renderizado como texto puro — sem cara de campo de formulario
                    — para deixar claro que nao pode ser alterado aqui. */}
                <div
                  className="mono"
                  title="O email é gerenciado pelo administrador"
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    flexWrap: "wrap",
                    gap: 8,
                    padding: "8px 2px",
                    color: "var(--ink)",
                    wordBreak: "break-all",
                  }}
                >
                  {usuario?.email || "—"}
                  <span
                    style={{
                      fontSize: "0.72rem",
                      fontWeight: 500,
                      color: "var(--ink-3)",
                    }}
                  >
                    · gerenciado pelo administrador
                  </span>
                </div>
              </div>
              <div className="input-group col-2">
                <label className="input-label">Descrição</label>
                <textarea
                  className="textarea"
                  placeholder="Uma breve descrição sobre você (opcional)…"
                  value={form.descricao}
                  onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
                />
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
              <AsyncButton
                variant="primary"
                onClick={() => updateProfile(form)}
                successMessage="Perfil atualizado."
              >
                Salvar perfil
              </AsyncButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
